import React from 'react';
import { NavLink } from 'react-router-dom';
import { useRBAC } from '../contexts/RBACContext';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  LayoutDashboard, 
  Users, 
  Shield, 
  FileText, 
  Calendar, 
  HardHat, 
  Wrench, 
  Truck, 
  DollarSign,
  FileBadge,
  Files,
  LogOut,
  X,
  Building,
  Building2,
  Activity,
  Handshake,
  Wallet,
  Home,
  Calculator,
  FileSpreadsheet,
  Factory,
  Sun,
  Moon,
  PenTool,
  TrendingUp,
  FileCheck,
  FileSignature,
  Database,
  BookOpen,
  Sparkles,
  PackageOpen,
  FolderTree,
  UserCheck,
  Scale,
  Box,
  MessageCircle,
  Briefcase
} from 'lucide-react';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { hasAccess, MODULES } = useRBAC();
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { projects, currentProject, setCurrentProject, isModuleEnabledForProject } = useProject();

  const navItems = [
    { name: 'Project Dashboard', path: '/', icon: LayoutDashboard, module: MODULES.DASHBOARD },
    { name: 'Digital Viewer (Lite)', path: '/digital-viewer', icon: Box, module: MODULES.DIGITAL_VIEWER },
    { name: 'Company Dashboard', path: '/company-dashboard', icon: Activity, module: MODULES.DASHBOARD, adminOnly: true },
    { name: 'Projects', path: '/projects', icon: Building2, module: MODULES.PROJECTS },
    { name: 'DPR', path: '/dpr', icon: FileText, module: MODULES.DPR },
    { name: 'Planning', path: '/planning', icon: Calendar, module: MODULES.PLANNING },
    { name: 'Labour', path: '/labour', icon: HardHat, module: MODULES.LABOUR },
    { name: 'Equipment', path: '/equipment', icon: Wrench, module: MODULES.EQUIPMENT },
    { name: 'Finance', path: '/finance', icon: DollarSign, module: MODULES.FINANCE },
    { name: 'BG & PBG', path: '/bg-management', icon: FileCheck, module: MODULES.BG_MANAGEMENT },
    { name: 'Tender Intelligence', path: '/tender-intelligence', icon: Sparkles, module: MODULES.TENDER_INTELLIGENCE },
    { name: 'Tender Management', path: '/tenders', icon: FileSignature, module: MODULES.TENDER_MANAGEMENT, projectTypeOnly: 'ho' },
    { name: 'Procurement & Vendor', path: '/procurement', icon: Truck, module: MODULES.PROCUREMENT_VENDOR, projectTypeOnly: 'ho' },
    { name: 'PO Management', path: '/po-management', icon: FileSignature, module: MODULES.PO_MANAGEMENT },

    { name: 'Test Reports', path: '/test-reports', icon: FileBadge, module: MODULES.TEST_REPORTS },
    { name: 'Documents', path: '/documents', icon: Files, module: MODULES.IMPORTANT_DOCUMENTS },
    { name: 'Store', path: '/store', icon: PackageOpen, module: MODULES.STORE },
    { name: 'Store Docs Explorer', path: '/store-document-explorer', icon: FileText, module: MODULES.STORE_DOCUMENT_EXPLORER },
    { name: 'Working', path: '/working', icon: Calculator, module: MODULES.WORKING },
    { name: 'Yard', path: '/yard', icon: Factory, module: MODULES.YARD },
    { name: 'BOQ', path: '/boq', icon: FileSpreadsheet, module: MODULES.BOQ },
    { name: 'Accountant Workbook', path: '/accountant-workbook', icon: FolderTree, module: MODULES.ACCOUNTANT_WORKBOOK },
    { name: 'Batching Plant', path: '/batching-plant', icon: Factory, module: MODULES.BATCHING_PLANT },
    { name: 'Analysis Dashboard', path: '/analysis', icon: LayoutDashboard, module: MODULES.ANALYSIS_DASHBOARD },
    { name: 'Subcontractor Rates', path: '/subcontractor-rates', icon: Handshake, module: MODULES.SUBCONTRACTOR_RATES },
    { name: 'Client Billing', path: '/client-billing', icon: Wallet, module: MODULES.CLIENT_BILLING },
    { name: 'Villa Sales', path: '/villa-sales', icon: Home, module: MODULES.VILLA_SALES },
    { name: 'Drawings', path: '/drawings', icon: PenTool, module: MODULES.DRAWINGS },
    { name: 'Project Control', path: '/progress-tracking', icon: TrendingUp, module: MODULES.PROGRESS_TRACKING },
    { name: 'Construction Tracker', path: '/construction-tracker', icon: Activity, module: MODULES.CONSTRUCTION_ACTIVITY_TRACKER },
    { name: 'Users', path: '/users', icon: Users, module: MODULES.USERS },
    { name: 'Roles', path: '/roles', icon: Shield, module: MODULES.ROLES },
    { name: 'Salary & Employees', path: '/salary', icon: Users, module: MODULES.SALARY },
    { name: 'Attendance', path: '/attendance', icon: UserCheck, module: MODULES.ATTENDANCE },
    { name: 'Weighbridge', path: '/weighbridge', icon: Scale, module: MODULES.WEIGHBRIDGE },
    { name: 'Item Knowledge Base', path: '/item-knowledge-base', icon: BookOpen, module: MODULES.ITEM_KNOWLEDGE_BASE, adminOnly: true },
    { name: 'AI Integration', path: '/ai-integration', icon: Sparkles, module: MODULES.AI_INTEGRATION, adminOnly: true },
    { name: 'WhatsApp Intelligence', path: '/whatsapp-intelligence', icon: MessageCircle, module: MODULES.WHATSAPP_INTELLIGENCE },
    { name: 'Recruitment', path: '/recruitment', icon: Briefcase, module: MODULES.RECRUITMENT },
  ];

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Sidebar Content */}
      <div className={`w-64 h-screen bg-bg-secondary border-r border-border-color flex flex-col fixed left-0 top-0 overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="p-6 border-b border-border-color flex justify-between items-start">
          <div className="w-full">
            <img src="/logo.png" alt="Dee Vee Projects" className="w-48 bg-white p-2 rounded mb-3 object-contain" />
            
            {/* Project Switcher */}
            <div className="mt-4">
              <label className="text-xs text-text-muted uppercase tracking-wider mb-1 block">Active Project</label>
              <div className="relative">
                <select
                  value={currentProject?.id || ''}
                  onChange={(e) => {
                    const selected = projects.find(p => p.id === e.target.value);
                    if (selected) setCurrentProject(selected);
                  }}
                  className="w-full bg-bg-tertiary border border-border-light text-text-primary text-sm rounded-md px-3 py-2 appearance-none outline-none focus:border-accent-primary"
                >
                  {projects.length === 0 && <option value="">No Projects Assigned</option>}
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.projectName}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted">
                  <Building size={14} />
                </div>
              </div>
            </div>

          </div>
          <button 
            className="md:hidden text-text-muted hover:text-text-primary"
            onClick={() => setIsOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            if (!hasAccess(item.module)) return null;
            if (item.adminOnly && !['Admin', 'Super Admin'].includes(currentUser?.role)) return null;
            
            // Check if module is enabled for the current project
            const isGlobalModule = [MODULES.DASHBOARD, MODULES.PROJECTS, MODULES.USERS, MODULES.ROLES, MODULES.ITEM_KNOWLEDGE_BASE, MODULES.AI_INTEGRATION, MODULES.WHATSAPP_INTELLIGENCE, MODULES.PO_MANAGEMENT, MODULES.TENDER_INTELLIGENCE].includes(item.module);
            if (!isGlobalModule && currentProject && !isModuleEnabledForProject(item.module)) return null;

            // Check specific project type restriction
            if (item.projectTypeOnly && currentProject && currentProject.projectType !== item.projectTypeOnly) return null;
            
            // Disable module links if no project is selected (except admin/global routes)
            const isGlobalRoute = ['/company-dashboard', '/projects', '/users', '/roles', '/item-knowledge-base', '/ai-integration', '/store', '/po-management', '/tender-intelligence'].includes(item.path);
            const isDisabled = !currentProject && !isGlobalRoute;

            return (
              <NavLink
                key={item.path}
                to={isDisabled ? '#' : item.path}
                onClick={(e) => {
                  if (isDisabled) e.preventDefault();
                  else setIsOpen(false);
                }}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${isActive && !isDisabled
                    ? 'bg-accent-primary text-white shadow-md' 
                    : 'text-text-secondary hover:bg-bg-tertiary hover:text-white'
                  }
                `}
              >
                <item.icon size={18} />
                <span className="font-medium text-sm">{item.name}</span>
              </NavLink>
            );
          })}
        </div>

        <div className="p-4 border-t border-border-color mt-auto bg-bg-secondary">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-accent-secondary flex items-center justify-center text-white font-bold shrink-0">
              {currentUser?.displayName?.charAt(0) || 'U'}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-text-primary truncate">{currentUser?.displayName}</p>
              <p className="text-xs text-text-muted truncate">{currentUser?.role}</p>
            </div>
          </div>
          
          <button 
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-4 py-2 mb-1 text-text-muted hover:text-text-primary transition-colors"
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            <span className="text-sm font-medium">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
          </button>

          <button 
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2 text-text-muted hover:text-status-danger transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
